import { CheckCircleOutline } from "@mui/icons-material";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  ReportMistake,
  ReportMistakes,
} from "../utils/studentReportMistakes";

export default function StudentDictationMistakes({
  report,
}: {
  report: ReportMistakes;
}): React.ReactElement {
  const { t } = useTranslation("texts");
  const label = (item: ReportMistake): string => {
    let value =
      item.kind === "incorrect"
        ? `${item.actual} → ${item.expected}`
        : item.expected || item.actual;
    if (item.kind === "punctuation")
      value = !item.actual
        ? t("students.report.mistakes.missingMark", { mark: item.expected })
        : !item.expected
          ? t("students.report.mistakes.extraMark", { mark: item.actual })
          : `«${item.actual}» → «${item.expected}»`;
    return item.count > 1 ? `${value} ×${item.count}` : value;
  };
  return (
    <Stack spacing={1.25}>
      {report.groups.map(
        (group): React.ReactElement => (
          <Box
            key={group.kind}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "190px minmax(0, 1fr)" },
              alignItems: "start",
              gap: 0.75,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ pt: { sm: 0.25 } }}
            >
              {t(`students.report.mistakes.${group.kind}`)} ({group.count})
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {group.items.map(
                (item): React.ReactElement => (
                  <Chip
                    key={JSON.stringify([item.expected, item.actual])}
                    size="small"
                    label={label(item)}
                    aria-label={
                      item.saved
                        ? `${label(item)} · ${t("students.wordSaved")}`
                        : label(item)
                    }
                    icon={item.saved ? <CheckCircleOutline /> : undefined}
                    variant="outlined"
                    color={item.saved ? "success" : "default"}
                    sx={{
                      maxWidth: "100%",
                      height: "auto",
                      minHeight: 24,
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        py: 0.25,
                      },
                    }}
                  />
                ),
              )}
            </Stack>
          </Box>
        ),
      )}
      {report.savedWords > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ color: "success.main" }}
        >
          <CheckCircleOutline sx={{ fontSize: 16 }} />
          <Typography variant="caption">{t("students.wordSaved")}</Typography>
        </Stack>
      )}
      {!report.groups.length && (
        <Typography variant="body2" color="text.secondary">
          {t("students.report.noWordErrors")}
        </Typography>
      )}
    </Stack>
  );
}
